// ============================================
// ActionButton Component
// Button variants: primary (gradient), secondary (outlined), ghost, dark
// ============================================

import { ReactNode, ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../utils/cn';

// ============================================
// Types
// ============================================

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'dark';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button style variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Optional icon on the left */
  icon?: ReactNode;
  /** Optional icon on the right */
  iconRight?: ReactNode;
  /** Loading state */
  isLoading?: boolean;
  /** Full width */
  fullWidth?: boolean;
  /** Children content */
  children?: ReactNode;
}

// ============================================
// Styles
// ============================================

// `whitespace-nowrap` + `shrink-0`: nhãn dài (vd "Bulk create products") trước đây
// xuống dòng bên trong nút có chiều cao cố định `h-10` nên chữ tràn ra ngoài viền,
// và trên mobile nút bị co lại tới mức chữ lòi khỏi khung. Giữ nút ở đúng bề rộng
// nội dung rồi để hàng chứa nó tự xuống dòng là cách duy nhất không vỡ khung.
const baseStyles =
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50';

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-[#0288D1] text-white shadow-sm hover:bg-[#0277BD] active:scale-[0.98]',
  secondary:
    'bg-white text-[#0288D1] border border-[#0288D1] shadow-[0_2px_4px_0_rgba(165,163,174,0.2)] hover:bg-blue-50 active:bg-blue-100',
  ghost:
    'text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200',
  dark:
    'bg-slate-900 text-white shadow-sm hover:bg-slate-800 active:bg-slate-950',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

// ============================================
// Component
// ============================================

export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      icon,
      iconRight,
      isLoading,
      fullWidth,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {isLoading ? (
          <>
            <LoadingSpinner />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {icon && <span className="shrink-0">{icon}</span>}
            {children}
            {iconRight && <span className="shrink-0">{iconRight}</span>}
          </>
        )}
      </button>
    );
  }
);

ActionButton.displayName = 'ActionButton';

// ============================================
// Loading Spinner
// ============================================

function LoadingSpinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
